import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  ShoppingCart,
  Search,
  Filter,
  Eye,
  Package,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  Download,
  MoreVertical,
  ChevronDown } from
'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle } from
'@/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger } from
"@/shared/components/ui/dropdown-menu";

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const STATUS_CONFIG = {
  draft: { icon: Clock, color: 'bg-gray-100 text-gray-700', label: 'Draft' },
  placed: { icon: ShoppingCart, color: 'bg-blue-100 text-blue-700', label: 'Placed' },
  validated: { icon: CheckCircle, color: 'bg-indigo-100 text-indigo-700', label: 'Validated' },
  approved: { icon: CheckCircle, color: 'bg-purple-100 text-purple-700', label: 'Approved' },
  paid: { icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700', label: 'Paid' },
  picking: { icon: Package, color: 'bg-amber-100 text-amber-700', label: 'Picking' },
  packed: { icon: Package, color: 'bg-orange-100 text-orange-700', label: 'Packed' },
  shipped: { icon: Truck, color: 'bg-cyan-100 text-cyan-700', label: 'Shipped' },
  delivered: { icon: CheckCircle, color: 'bg-teal-100 text-teal-700', label: 'Delivered' },
  completed: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Completed' },
  cancelled: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Cancelled' },
  refunded: { icon: RefreshCw, color: 'bg-pink-100 text-pink-700', label: 'Refunded' }
};

export const EcommerceOrders = () => {const { t } = useTranslation("ecommerce");
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/ecommerce/orders?limit=50`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = async (orderId) => {
    try {
      const res = await fetch(`${API_URL}/api/ecommerce/orders/${orderId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSelectedOrder(data);
        setShowDetailModal(true);
      }
    } catch (err) {
      toast.error('Failed to load order details');
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/ecommerce/orders/${orderId}/status?new_status=${newStatus}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success(`Order status updated to ${newStatus}`);
        fetchOrders();
        if (selectedOrder?.id === orderId) {
          setSelectedOrder({ ...selectedOrder, status: newStatus });
        }
      }
    } catch (err) {
      toast.error('Failed to update order status');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredOrders = orders.filter((o) =>
  searchQuery === '' ||
  o.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
  o.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
  o.customer_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const orderCounts = {
    all: orders.length,
    pending: orders.filter((o) => ['draft', 'placed', 'validated'].includes(o.status)).length,
    processing: orders.filter((o) => ['approved', 'paid', 'picking', 'packed'].includes(o.status)).length,
    shipped: orders.filter((o) => ['shipped', 'delivered'].includes(o.status)).length,
    completed: orders.filter((o) => o.status === 'completed').length
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="ecommerce-orders">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
              <ShoppingCart className="h-7 w-7 text-white" />
            </div>{t("orders", "Orders")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageAndFulfill", "Manage and fulfill customer orders across all channels")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={fetchOrders}>
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
        { key: 'all', label: 'All Orders', color: 'bg-gray-100' },
        { key: 'pending', label: 'Pending', color: 'bg-amber-100' },
        { key: 'processing', label: 'Processing', color: 'bg-blue-100' },
        { key: 'shipped', label: 'Shipped', color: 'bg-cyan-100' },
        { key: 'completed', label: 'Completed', color: 'bg-emerald-100' }].
        map((stat) =>
        <button
          key={stat.key}
          onClick={() => setStatusFilter(stat.key === 'all' ? 'all' : stat.key)}
          className={`p-3 rounded-xl text-center transition-all ${
          statusFilter === stat.key ? 'ring-2 ring-emerald-500 ring-offset-2' : ''} ${
          stat.color}`}>
          
            <p className="text-2xl font-bold">{orderCounts[stat.key]}</p>
            <p className="text-xs text-muted-foreground">{stat.label}</p>
          </button>
        )}
      </div>

      {/* Filters */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search orders by number, customer name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
              
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) =>
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className="card-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="py-3 px-4 text-left text-sm font-medium">{t("order", "Order")}</th>
                <th className="py-3 px-4 text-left text-sm font-medium">{t("customer", "Customer")}</th>
                <th className="py-3 px-4 text-left text-sm font-medium">{t("channel", "Channel")}</th>
                <th className="py-3 px-4 text-left text-sm font-medium">{t("status", "Status")}</th>
                <th className="py-3 px-4 text-right text-sm font-medium">{t("total", "Total")}</th>
                <th className="py-3 px-4 text-left text-sm font-medium">{t("date", "Date")}</th>
                <th className="py-3 px-4 text-center text-sm font-medium">{t("actions", "Actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredOrders.map((order) => {
                const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.draft;
                const StatusIcon = statusConfig.icon;

                return (
                  <tr key={order.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-mono font-medium text-sm">{order.order_number}</p>
                        <p className="text-xs text-muted-foreground">{order.items_count || 1}{t("items", "items")}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-medium">
                          {order.customer_name?.charAt(0) || 'C'}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{order.customer_name}</p>
                          <p className="text-xs text-muted-foreground">{order.customer_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className="capitalize">
                        {order.channel?.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={`gap-1 ${statusConfig.color}`}>
                        <StatusIcon className="h-3 w-3" />
                        {statusConfig.label}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <p className="font-mono font-semibold">{formatCurrency(order.total)}</p>
                      <p className="text-xs text-muted-foreground">{order.payment_method}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm">{formatDate(order.created_at)}</p>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1"
                          onClick={() => handleViewOrder(order.id)}>
                          
                          <Eye className="h-4 w-4" />{t("view", "View")}

                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleUpdateStatus(order.id, 'paid')}>{t("markAsPaid", "Mark as Paid")}

                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateStatus(order.id, 'shipped')}>{t("markAsShipped", "Mark as Shipped")}

                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateStatus(order.id, 'completed')}>{t("markAsCompleted", "Mark as Completed")}

                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateStatus(order.id, 'cancelled')} className="text-red-600">{t("cancelOrder", "Cancel Order")}

                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>);

              })}
            </tbody>
          </table>
        </div>
        
        {filteredOrders.length === 0 &&
        <div className="p-12 text-center">
            <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">{t("noOrdersFound", "No orders found")}</p>
          </div>
        }
      </Card>

      {/* Order Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-blue-600" />{t("order", "Order")}
              {selectedOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder &&
          <div className="space-y-6 py-4">
              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div>
                  <p className="text-sm text-muted-foreground">{t("status", "Status")}</p>
                  <Badge className={`mt-1 ${STATUS_CONFIG[selectedOrder.status]?.color}`}>
                    {STATUS_CONFIG[selectedOrder.status]?.label}
                  </Badge>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">{t("total", "Total")}</p>
                  <p className="text-2xl font-bold">{formatCurrency(selectedOrder.total)}</p>
                </div>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">{t("customer", "Customer")}</p>
                  <p className="font-medium">{selectedOrder.customer_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedOrder.customer_email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">{t("payment", "Payment")}</p>
                  <p className="font-medium capitalize">{selectedOrder.payment_method}</p>
                  <Badge variant="outline" className="mt-1">
                    {selectedOrder.payment_status}
                  </Badge>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-3">{t("items", "Items")}</p>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) =>
                <div key={idx} className="flex items-center gap-3 p-3 border rounded-lg">
                      <img
                    src={item.image}
                    alt={item.name}
                    className="w-12 h-12 object-cover rounded-lg" />
                  
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.name}</p>
                        <p className="text-xs text-muted-foreground">{t("sku", "SKU:")}{item.sku}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrency(item.unit_price)}</p>
                        <p className="text-xs text-muted-foreground">{t("qty", "Qty:")}{item.quantity}</p>
                      </div>
                    </div>
                )}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("subtotal", "Subtotal")}</span>
                  <span>{formatCurrency(selectedOrder.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("shipping", "Shipping")}</span>
                  <span>{formatCurrency(selectedOrder.shipping_total)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("tax", "Tax")}</span>
                  <span>{formatCurrency(selectedOrder.tax_total)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>{t("total", "Total")}</span>
                  <span>{formatCurrency(selectedOrder.total)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <Select onValueChange={(status) => handleUpdateStatus(selectedOrder.id, status)}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Update Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) =>
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  )}
                  </SelectContent>
                </Select>
                <Button variant="outline">{t("printInvoice", "Print Invoice")}</Button>
              </div>
            </div>
          }
        </DialogContent>
      </Dialog>
    </div>);

};

export default EcommerceOrders;